import { Color } from 'excalibur';

export interface ThemePalette {
  paper: Color;
  paperDeep: Color;
  paperEdge: Color;
  ink: Color;
  inkSoft: Color;
  blue: Color;
  blueDeep: Color;
  pink: Color;
  pinkDeep: Color;
  mint: Color;
  mintDeep: Color;
  yellow: Color;
  yellowDeep: Color;
  cream: Color;
  heart: Color;
  heartDeep: Color;
  shadow: Color;
  ember: Color;
  flame: Color;
  flameDeep: Color;
  deepInk: Color;
  tooth: Color;
  mouth: Color;
}

export interface ThemePaper {
  washes: Color[];
  speckle: Color;
  vignette: Color;
  cornerInk: Color;
}

export interface Theme {
  id: string;
  name: string;
  tagline: string;
  price: number;
  palette: ThemePalette;
  paper: ThemePaper;
  doodleColors: Color[];
}

function palette(values: Record<keyof ThemePalette, string>): ThemePalette {
  return {
    paper: Color.fromHex(values.paper),
    paperDeep: Color.fromHex(values.paperDeep),
    paperEdge: Color.fromHex(values.paperEdge),
    ink: Color.fromHex(values.ink),
    inkSoft: Color.fromHex(values.inkSoft),
    blue: Color.fromHex(values.blue),
    blueDeep: Color.fromHex(values.blueDeep),
    pink: Color.fromHex(values.pink),
    pinkDeep: Color.fromHex(values.pinkDeep),
    mint: Color.fromHex(values.mint),
    mintDeep: Color.fromHex(values.mintDeep),
    yellow: Color.fromHex(values.yellow),
    yellowDeep: Color.fromHex(values.yellowDeep),
    cream: Color.fromHex(values.cream),
    heart: Color.fromHex(values.heart),
    heartDeep: Color.fromHex(values.heartDeep),
    shadow: Color.fromHex(values.shadow),
    ember: Color.fromHex(values.ember),
    flame: Color.fromHex(values.flame),
    flameDeep: Color.fromHex(values.flameDeep),
    deepInk: Color.fromHex(values.deepInk),
    tooth: Color.fromHex(values.tooth),
    mouth: Color.fromHex(values.mouth),
  };
}

const SKETCHBOOK = palette({
  paper: '#FBF4E6',
  paperDeep: '#F4EAD6',
  paperEdge: '#E8DABE',
  ink: '#6B5C4A',
  inkSoft: '#9C8C77',
  blue: '#A9CFE8',
  blueDeep: '#6FA6C7',
  pink: '#F6B8C8',
  pinkDeep: '#DE8CAB',
  mint: '#B7DFC6',
  mintDeep: '#7FBC9A',
  yellow: '#F7E3A1',
  yellowDeep: '#DCBE5C',
  cream: '#FFF9EC',
  heart: '#F29FB4',
  heartDeep: '#D9778F',
  shadow: '#C6B394',
  ember: '#F0964A',
  flame: '#F8CE63',
  flameDeep: '#E06B3C',
  deepInk: '#43362B',
  tooth: '#FFF9EC',
  mouth: '#3F3128',
});

const MIDNIGHT = palette({
  paper: '#2A2735',
  paperDeep: '#332F42',
  paperEdge: '#1E1B28',
  ink: '#EDE4D6',
  inkSoft: '#A79FB5',
  blue: '#8FB6F0',
  blueDeep: '#6E93D6',
  pink: '#F79BC0',
  pinkDeep: '#D9739F',
  mint: '#86D9B4',
  mintDeep: '#5DB392',
  yellow: '#F3D98A',
  yellowDeep: '#D9B85E',
  cream: '#46405C',
  heart: '#F58AAE',
  heartDeep: '#D46A8E',
  shadow: '#14121C',
  ember: '#F09A4D',
  flame: '#FFD166',
  flameDeep: '#E0642F',
  deepInk: '#0F0D16',
  tooth: '#EFE6D8',
  mouth: '#241E30',
});

const MINT_CHIP = palette({
  paper: '#EEF7F0',
  paperDeep: '#E2F0E6',
  paperEdge: '#CDE4D4',
  ink: '#43584B',
  inkSoft: '#7C9184',
  blue: '#A9D3E4',
  blueDeep: '#6FA8C4',
  pink: '#F5C3D4',
  pinkDeep: '#DB94B0',
  mint: '#A9E2C2',
  mintDeep: '#6FBB92',
  yellow: '#F4E4A6',
  yellowDeep: '#D8BC62',
  cream: '#FBFDF9',
  heart: '#F09FB4',
  heartDeep: '#D4778F',
  shadow: '#B9CFC0',
  ember: '#EE9B52',
  flame: '#F6CF6B',
  flameDeep: '#DD6F3E',
  deepInk: '#2F4038',
  tooth: '#FBFDF9',
  mouth: '#2F4038',
});

const CANDY = palette({
  paper: '#FFF1F7',
  paperDeep: '#FCE6EF',
  paperEdge: '#F3CFDF',
  ink: '#6A4A5C',
  inkSoft: '#A3889A',
  blue: '#B9D9F2',
  blueDeep: '#86A8D6',
  pink: '#F9BBD5',
  pinkDeep: '#E083AE',
  mint: '#BFE8D6',
  mintDeep: '#86C4A8',
  yellow: '#FBE6A8',
  yellowDeep: '#E3BE62',
  cream: '#FFFBFE',
  heart: '#F58FB4',
  heartDeep: '#D96A92',
  shadow: '#D8B8C6',
  ember: '#F59A5A',
  flame: '#FAD06E',
  flameDeep: '#E2734A',
  deepInk: '#4A3340',
  tooth: '#FFFBFE',
  mouth: '#4A3340',
});

export const SKINS: Theme[] = [
  {
    id: 'sketchbook',
    name: 'Sketchbook',
    tagline: 'the classic doodle',
    price: 0,
    palette: SKETCHBOOK,
    paper: {
      washes: [SKETCHBOOK.yellow, SKETCHBOOK.mint, SKETCHBOOK.pink, SKETCHBOOK.blue],
      speckle: SKETCHBOOK.ink,
      vignette: SKETCHBOOK.paperEdge,
      cornerInk: SKETCHBOOK.inkSoft,
    },
    doodleColors: [SKETCHBOOK.mintDeep, SKETCHBOOK.yellowDeep, SKETCHBOOK.pinkDeep, SKETCHBOOK.blueDeep],
  },
  {
    id: 'midnight',
    name: 'Midnight',
    tagline: 'chalk on a blackboard',
    price: 199,
    palette: MIDNIGHT,
    paper: {
      washes: [MIDNIGHT.blue, MIDNIGHT.pink, MIDNIGHT.mint, MIDNIGHT.yellow],
      speckle: MIDNIGHT.ink,
      vignette: MIDNIGHT.shadow,
      cornerInk: MIDNIGHT.inkSoft,
    },
    doodleColors: [MIDNIGHT.mintDeep, MIDNIGHT.yellowDeep, MIDNIGHT.pinkDeep, MIDNIGHT.blueDeep],
  },
  {
    id: 'mint-chip',
    name: 'Mint Chip',
    tagline: 'cool and cozy',
    price: 199,
    palette: MINT_CHIP,
    paper: {
      washes: [MINT_CHIP.mint, MINT_CHIP.blue, MINT_CHIP.yellow, MINT_CHIP.pink],
      speckle: MINT_CHIP.ink,
      vignette: MINT_CHIP.paperEdge,
      cornerInk: MINT_CHIP.inkSoft,
    },
    doodleColors: [MINT_CHIP.mintDeep, MINT_CHIP.yellowDeep, MINT_CHIP.pinkDeep, MINT_CHIP.blueDeep],
  },
  {
    id: 'candy',
    name: 'Candy',
    tagline: 'strawberry frosting',
    price: 199,
    palette: CANDY,
    paper: {
      washes: [CANDY.pink, CANDY.blue, CANDY.yellow, CANDY.mint],
      speckle: CANDY.ink,
      vignette: CANDY.paperEdge,
      cornerInk: CANDY.inkSoft,
    },
    doodleColors: [CANDY.pinkDeep, CANDY.blueDeep, CANDY.yellowDeep, CANDY.mintDeep],
  },
];
